import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Clock3,
  MessageCircleQuestion,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { Container } from "@/components/Container";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { submitLiveReviewInterest, trackEvent } from "@/lib/api";
import { trackGoogleEvent } from "@/lib/googleTracking";
import { useStudyAuth } from "@/context/StudyAuth";

const HELP_OPTIONS = [
  { value: "group", label: "Group workshop", detail: "A live small-group review and Q&A" },
  { value: "private", label: "Private tutoring", detail: "Individual help focused on your weak areas" },
  { value: "both", label: "Both", detail: "Keep me informed about either option" },
];

const TIME_OPTIONS = [
  { value: "flexible", label: "I am flexible" },
  { value: "weekday_evening", label: "Weekday evening" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

const TOPIC_OPTIONS = [
  "Illinois agency law",
  "Licensing requirements",
  "Escrow, disclosures, and state rules",
  "Contracts and national concepts",
  "Real estate math",
  "Exam strategy and pacing",
  "Not sure yet",
];

const ALLOWED_SOURCES = new Set(["product", "dashboard", "website"]);

export default function LiveReview() {
  const [searchParams] = useSearchParams();
  const sourceParam = searchParams.get("source") || "website";
  const source = ALLOWED_SOURCES.has(sourceParam) ? sourceParam : "website";
  const { email: accountEmail, license } = useStudyAuth();
  const [form, setForm] = useState({
    name: (license?.first_name || "").trim(),
    email: accountEmail || license?.email || "",
    exam_date: "",
    preferred_time: "flexible",
    hardest_topic: "",
    help_type: "group",
    consent_to_contact: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm((current) => ({
      ...current,
      name: current.name || (license?.first_name || "").trim(),
      email: current.email || accountEmail || license?.email || "",
    }));
  }, [accountEmail, license?.email, license?.first_name]);

  const returnPath = source === "dashboard" ? "/study/dashboard" : "/product";
  const returnLabel = source === "dashboard" ? "Back to dashboard" : "Back to study platform";
  const selectedHelp = useMemo(
    () => HELP_OPTIONS.find((option) => option.value === form.help_type),
    [form.help_type]
  );

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.consent_to_contact) {
      setError("Please confirm that we may email you about workshop dates and availability.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await submitLiveReviewInterest({
        ...form,
        exam_date: form.exam_date || null,
        source,
      });
      trackEvent("live_review_interest_submit", { source, help_type: form.help_type });
      trackGoogleEvent("live_review_interest_submit", {
        source,
        help_type: form.help_type,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "We could not save your preferences. Please check your information and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[70vh] bg-[var(--ibp-offwhite)] py-16 sm:py-24">
        <Container>
          <div className="mx-auto max-w-2xl border border-[color:rgba(10,27,51,0.12)] bg-white px-6 py-10 text-center sm:px-10">
            <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-[color:rgba(31,138,91,0.12)] text-[#1F8A5B]">
              <CheckCircle2 className="h-6 w-6" />
            </span>
            <h1 className="mt-5 text-3xl font-bold text-[var(--ibp-ink)]">
              You are on the priority list.
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-[color:rgba(11,18,32,0.68)]">
              We saved your exam timing and study preferences. We will email you when a relevant
              live-review date or tutoring option is available. No payment has been collected.
            </p>
            <Link
              to={returnPath}
              className="mt-7 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[var(--ibp-navy-950)] px-5 py-3 text-sm font-semibold text-white"
            >
              <ArrowLeft className="h-4 w-4" /> {returnLabel}
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-16">
        <Container>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E7BE4A]">
            Live review and tutoring
          </p>
          <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight sm:text-4xl">
            Join an upcoming Illinois Exam Final Review.
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-white/75 sm:text-base">
            Share your expected exam date and toughest topics. We will notify you when a live
            small-group review aligned with your timeline opens for enrollment.
          </p>
        </Container>
      </section>

      <Container className="py-12 sm:py-16">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr),320px] lg:gap-14">
          <form onSubmit={handleSubmit} className="border border-[color:rgba(10,27,51,0.12)] bg-white p-6 sm:p-8">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="live-review-name" className="text-[var(--ibp-ink)]">Full name</Label>
                <Input
                  id="live-review-name"
                  value={form.name}
                  onChange={(event) => setField("name", event.target.value)}
                  autoComplete="name"
                  maxLength={120}
                  required
                  className="mt-2 h-11 rounded-lg bg-white"
                />
              </div>
              <div>
                <Label htmlFor="live-review-email" className="text-[var(--ibp-ink)]">Email</Label>
                <Input
                  id="live-review-email"
                  type="email"
                  value={form.email}
                  onChange={(event) => setField("email", event.target.value)}
                  autoComplete="email"
                  maxLength={254}
                  required
                  className="mt-2 h-11 rounded-lg bg-white"
                />
              </div>
              <div>
                <Label htmlFor="live-review-exam-date" className="text-[var(--ibp-ink)]">
                  Expected exam date <span className="font-normal text-[color:rgba(11,18,32,0.48)]">(optional)</span>
                </Label>
                <Input
                  id="live-review-exam-date"
                  type="date"
                  value={form.exam_date}
                  onChange={(event) => setField("exam_date", event.target.value)}
                  className="mt-2 h-11 rounded-lg bg-white"
                />
              </div>
              <div>
                <Label htmlFor="live-review-time" className="text-[var(--ibp-ink)]">Preferred timing</Label>
                <select
                  id="live-review-time"
                  value={form.preferred_time}
                  onChange={(event) => setField("preferred_time", event.target.value)}
                  className="mt-2 h-11 w-full rounded-lg border border-input bg-white px-3 text-sm text-[var(--ibp-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ibp-focus-ring)]"
                >
                  {TIME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="live-review-topic" className="text-[var(--ibp-ink)]">Hardest topic right now</Label>
              <select
                id="live-review-topic"
                value={form.hardest_topic}
                onChange={(event) => setField("hardest_topic", event.target.value)}
                required
                className="mt-2 h-11 w-full rounded-lg border border-input bg-white px-3 text-sm text-[var(--ibp-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ibp-focus-ring)]"
              >
                <option value="" disabled>Select a topic</option>
                {TOPIC_OPTIONS.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
              </select>
            </div>

            <fieldset className="mt-6">
              <legend className="text-sm font-medium text-[var(--ibp-ink)]">What kind of help interests you?</legend>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                {HELP_OPTIONS.map((option) => {
                  const selected = option.value === form.help_type;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setField("help_type", option.value)}
                      className={[
                        "min-h-[104px] border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ibp-focus-ring)]",
                        selected
                          ? "border-[var(--ibp-navy-950)] bg-[var(--ibp-navy-950)] text-white"
                          : "border-[color:rgba(10,27,51,0.14)] bg-white text-[var(--ibp-ink)] hover:bg-[var(--ibp-offwhite)]",
                      ].join(" ")}
                    >
                      <span className="block text-sm font-semibold">{option.label}</span>
                      <span className={selected ? "mt-1 block text-xs leading-5 text-white/70" : "mt-1 block text-xs leading-5 text-[color:rgba(11,18,32,0.56)]"}>
                        {option.detail}
                      </span>
                    </button>
                  );
                })}
              </div>
              <input type="hidden" name="help_type" value={selectedHelp?.value || "group"} />
            </fieldset>

            <label className="mt-6 flex cursor-pointer items-start gap-3 text-sm leading-6 text-[color:rgba(11,18,32,0.72)]">
              <input
                type="checkbox"
                checked={form.consent_to_contact}
                onChange={(event) => setField("consent_to_contact", event.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-[var(--ibp-navy-950)]"
              />
              <span>Email me about live-review dates and availability. I understand this is an interest list, not a purchase or reservation.</span>
            </label>

            {error ? (
              <p role="alert" className="mt-4 border-l-4 border-l-[#B42318] bg-[color:rgba(180,35,24,0.06)] px-4 py-3 text-sm text-[#8f2118]">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 inline-flex min-h-12 w-full items-center justify-center rounded-xl bg-[var(--ibp-gold-500)] px-6 py-3 text-sm font-bold text-[var(--ibp-ink)] transition-colors hover:bg-[var(--ibp-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ibp-focus-ring)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {submitting ? "Saving your preferences..." : "Join the priority list"}
            </button>
          </form>

          <aside className="space-y-7 lg:pt-2">
            <div>
              <h2 className="text-base font-semibold text-[var(--ibp-ink)]">Choose the support that fits your plan</h2>
              <ul className="mt-4 space-y-4 text-sm leading-6 text-[color:rgba(11,18,32,0.7)]">
                <li className="flex gap-3"><UsersRound className="mt-0.5 h-5 w-5 flex-none text-[#B7791F]" /><span>Focused small-group final review.</span></li>
                <li className="flex gap-3"><CalendarDays className="mt-0.5 h-5 w-5 flex-none text-[#B7791F]" /><span>Session timing aligned with student exam dates.</span></li>
                <li className="flex gap-3"><MessageCircleQuestion className="mt-0.5 h-5 w-5 flex-none text-[#B7791F]" /><span>Illinois and national topic review with live Q&amp;A.</span></li>
                <li className="flex gap-3"><Clock3 className="mt-0.5 h-5 w-5 flex-none text-[#B7791F]" /><span>Private tutoring availability for focused support.</span></li>
              </ul>
            </div>
            <div className="border-t border-[color:rgba(10,27,51,0.12)] pt-6">
              <p className="flex gap-3 text-xs leading-6 text-[color:rgba(11,18,32,0.58)]">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-[#1F8A5B]" />
                Joining the priority list does not reserve a seat or collect payment. Available dates, instructor details, and pricing are provided before you book.
              </p>
            </div>
          </aside>
        </div>
      </Container>
    </div>
  );
}
